// src/api/aiComposition.js
import axios from 'axios';

const AI_COMPOSITION_API_URL = '/api/ai-composition';

/**
 * Send selected sticky notes with domain/tag metadata to AI for sophisticated composition
 * @param {Array} stickies - Array of sticky notes with domain/tag data
 * @param {string} mode - Composition mode (compose_note, outline, extract_claims, summarize)
 * @param {Object} noteContext - Note context for insertion
 * @param {Object} constraints - Optional constraints for AI processing
 * @returns {Promise<Object>} - Structured AI composition result
 */
export const sendStickiesToAIComposition = async (
  stickies, 
  mode = "compose_note",
  noteContext = { note_id: 1, note_title: "Untitled Note" },
  constraints = {}
) => {
  try {
    if (!stickies.length) {
      console.warn("No stickies selected for AI composition.");
      return null;
    }

    console.log("Sending AI Composition Request with", stickies.length, "stickies");

    // Build payload with domain/tag metadata
    const payload = {
      stickies: stickies.map((sticky, index) => ({
        id: sticky.id,
        title: sticky.title || 'Untitled',
        content: sticky.content || '',
        tag1: sticky.tag1 || null,  // domain
        tag2: sticky.tag2 || null,  // specific tag
        created_at: sticky.created_at || new Date().toISOString(),
        order: sticky.list_order || index
      })),
      mode,
      note_context: {
        note_id: noteContext.note_id || 1,
        note_title: noteContext.note_title || "Untitled Note"
      },
      global_tags: [], // Can be expanded later
      constraints: {
        max_tokens: 1200,
        style: "concise_technical",
        audience: "knowledge_worker",
        ...constraints
      }
    };

    console.log("AI Composition Payload:", payload);

    const response = await axios.post(AI_COMPOSITION_API_URL, payload, {
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    console.log("AI Composition Response received:", response.data);

    return response.data; // Should be structured JSON with title, summary, claims, etc.

  } catch (error) {
    console.error("Error sending stickies to AI composition:", error);

    // Handle specific error codes
    if (error.response?.status === 402) {
      console.warn("Token balance error");
      throw new Error("Insufficient token balance");
    }

    if (error.response?.status === 401) {
      console.warn("Authentication error");
      throw new Error("Authentication failed");
    }

    if (error.response?.status === 500) {
      console.warn("AI composition failed - invalid response");
      throw new Error("AI returned invalid response format");
    }

    throw new Error("AI composition failed");
  }
};

/**
 * Available composition modes
 */
export const COMPOSITION_MODES = {
  COMPOSE_NOTE: "compose_note",
  OUTLINE: "outline", 
  EXTRACT_CLAIMS: "extract_claims",
  SUMMARIZE: "summarize"
};